/**
 * A screenshot being prepared for a reported Issue (browser only): the image the reporter
 * captured or uploaded, the marks they drew (still editable), and the parts the submit
 * uploads.
 *
 * Nothing is flattened until submit. Then:
 *   - `screenshot`   — the marks burned in at natural size (the plain image if none), under
 *                      the per-part cap, downscaled rather than refused;
 *   - `original`     — the un-marked image, ONLY when marks were added and none is a Redact
 *                      (a redaction must leave no un-redacted copy anywhere);
 *   - `delyverImage` — the marked image again, at most 1,600 px and under 2 MB, for Delyver.
 */

import {
  DELYVER_IMAGE_MAX_BYTES,
  DELYVER_IMAGE_MAX_EDGE,
  SCREENSHOT_MAX_BYTES,
} from "@/schemas/support-issues";
import { hasRedaction, type Mark, textNotes } from "./marks";
import { encodeUnder, imageToCanvas, loadImage, renderMarked } from "./render";

export interface ScreenshotDraft {
  source: Blob;
  /** Object URL of `source` — revoke with `disposeDraft`. */
  sourceUrl: string;
  width: number;
  height: number;
  origin: "capture" | "upload";
  marks: Mark[];
  /** Object URL of the marked preview, when there are marks. */
  previewUrl: string | null;
}

export async function createDraft(
  source: Blob,
  origin: "capture" | "upload",
): Promise<ScreenshotDraft> {
  const image = await loadImage(source);
  return {
    source,
    sourceUrl: URL.createObjectURL(source),
    width: image.naturalWidth,
    height: image.naturalHeight,
    origin,
    marks: [],
    previewUrl: null,
  };
}

export function disposeDraft(draft: ScreenshotDraft | null): void {
  if (!draft) return;
  URL.revokeObjectURL(draft.sourceUrl);
  if (draft.previewUrl) URL.revokeObjectURL(draft.previewUrl);
}

/** Apply edited marks and refresh the preview. Disposes the old preview URL. */
export async function withMarks(draft: ScreenshotDraft, marks: Mark[]): Promise<ScreenshotDraft> {
  if (draft.previewUrl) URL.revokeObjectURL(draft.previewUrl);
  if (marks.length === 0) return { ...draft, marks, previewUrl: null };
  const canvas = await renderMarked(await loadImage(draft.source), marks);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  return { ...draft, marks, previewUrl: blob ? URL.createObjectURL(blob) : null };
}

export interface ScreenshotParts {
  screenshot: Blob;
  original: Blob | null;
  delyverImage: Blob;
  markedUp: boolean;
  redacted: boolean;
  notes: string[];
}

export async function prepareScreenshotParts(draft: ScreenshotDraft): Promise<ScreenshotParts> {
  const image = await loadImage(draft.source);
  const markedUp = draft.marks.length > 0;
  const redacted = hasRedaction(draft.marks);
  const marked = markedUp ? await renderMarked(image, draft.marks) : imageToCanvas(image);
  const screenshot = await encodeUnder(marked, SCREENSHOT_MAX_BYTES);
  const original =
    markedUp && !redacted ? await encodeUnder(imageToCanvas(image), SCREENSHOT_MAX_BYTES) : null;
  const delyverImage = await encodeUnder(marked, DELYVER_IMAGE_MAX_BYTES, DELYVER_IMAGE_MAX_EDGE);
  return { screenshot, original, delyverImage, markedUp, redacted, notes: textNotes(draft.marks) };
}

export function fileExtension(blob: Blob): string {
  if (blob.type === "image/jpeg") return "jpg";
  if (blob.type === "image/webp") return "webp";
  return "png";
}
